import { FootnoteItem, getFootnoteStoreConnection, subscribeFootnoteStore } from '@dh-io-emh/footnotes/dist';
import { infuse } from '../ioc-config';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { keyIterator } from '../utils/key-iterator.util';
import { useI18n } from './i18n.composable';
import { Logger } from '../utils/logger.util';

export { FOOTNOTE_CONTAINER_ID } from '@dh-io-emh/footnotes/dist';

const footnotes = ref<FootnoteItem[]>([]);

// Subscribe to the footnote store and store footnotes in a reactive ref
// (e.g. footnotes were added/removed by another component, potentially changing reference numbers)
subscribeFootnoteStore((state) => {
  footnotes.value = [...state.contexts.default];
});

// Composable that creates a bridge between the Seamless footnotes store and our application
export const useFootnoteStore = () => {
  const addFootnotes = async (texts: string[], nonIndexed = false): Promise<FootnoteItem[]> => {
    const connection = await getFootnoteStoreConnection();

    connection.addFootnotes({ footnotes: texts.map((text) => ({ text, nonIndexed })) });

    return footnotes.value.filter((footnote) => texts.includes(footnote.text));
  };

  const addFootnote = async (text: string, nonIndexed = false): Promise<FootnoteItem> => {
    const footnotes = await addFootnotes([text], nonIndexed);

    return footnotes[0];
  };

  const removeFootnotes = async (texts: string[]): Promise<void> => {
    const connection = await getFootnoteStoreConnection();

    connection.removeFootnotes({ footnotes: texts.map((text) => ({ text })) });
  };

  const removeFootnote = async (text: string): Promise<void> => {
    await removeFootnotes([text]);
  };

  return {
    addFootnote,
    addFootnotes,
    removeFootnote,
    removeFootnotes,
    footnotes: computed(() => footnotes.value),
  };
};

// Register non-indexed footnotes
export function useNonIndexedFootnotes(page?: string, includeGlobal = false) {
  const { exists } = useI18n();
  const {
    footnotes: { isPilot },
  } = infuse('config');
  const nonIndexedFootnotes = ref<string[]>([]);
  const nonIndexedFootnotesGroup = computed(() => replaceInvalidTags(joinWithParagraphTags(nonIndexedFootnotes.value)));

  const setNonIndexedFootnotes = (...footnotesPhrase: string[]) => {
    // Remove duplicates from input array and search for duplicate in existing non-indexed footnotes state
    const footnotesSet = [...new Set(footnotesPhrase)]?.filter((text) => !nonIndexedFootnotes.value.includes(text));

    nonIndexedFootnotes.value.push(...footnotesSet);
  };

  const setFootnoteElementDescription = (value?: string) => {
    const footnotesInPageDocument = document.getElementsByTagName('mbmxp-footnote-component');
    const [foundGlobalFootnoteComponent] = [...footnotesInPageDocument].filter(
      (component) => component.getAttribute('type') === 'global',
    );

    if (!foundGlobalFootnoteComponent) {
      Logger.warn('no footnote container element found in the page');

      return;
    }

    foundGlobalFootnoteComponent.setAttribute('description', value ?? nonIndexedFootnotesGroup.value);
  };

  const useFootnotes = () => {
    if (isPilot) {
      return {
        showFootnotes: setFootnoteElementDescription,
        hideFootnotes: () => {
          nonIndexedFootnotes.value = [];
          setFootnoteElementDescription('');
        },
      };
    } else {
      const { addFootnotes, removeFootnotes } = useFootnoteStore();

      return {
        showFootnotes: () => addFootnotes(nonIndexedFootnotes.value, true),
        hideFootnotes: () => removeFootnotes(nonIndexedFootnotes.value),
      };
    }
  };

  const { showFootnotes, hideFootnotes } = useFootnotes();

  // Get all global non-indexed footnotes
  const globalFootnotesPhraseKey = 'dcp.footnotes.nonIndexed';

  if (includeGlobal && exists(globalFootnotesPhraseKey, { detectEmpty: true })) {
    setNonIndexedFootnotes(...keyIterator(globalFootnotesPhraseKey));
  }

  // Get page related footnotes
  const pageFootnotesPhraseKey = `dcp.${page}.footnotes.nonIndexed`;

  if (!!page && exists(pageFootnotesPhraseKey, { detectEmpty: true })) {
    setNonIndexedFootnotes(...keyIterator(pageFootnotesPhraseKey));
  }

  // Only render footnotes component when page is passed
  if (page) {
    onMounted(() => {
      showFootnotes();
    });

    onBeforeUnmount(() => {
      hideFootnotes();
    });
  }

  return {
    nonIndexedFootnotes,
    nonIndexedFootnotesGroup,
  };
}

const joinWithParagraphTags = (texts: string[]) => {
  const startTag = '<p>';
  const endTag = '</p>';

  return texts.reduce((previousText, currentText) => {
    return `${previousText}${startTag}${currentText}${endTag}`;
  }, '');
};

const replaceInvalidTags = (text: string) => {
  const rules = [['h2', 'h4']] as const;

  return rules.reduce((previousText, [searchBy, replaceWith]) => {
    return previousText.replace(searchBy, replaceWith);
  }, text);
};
